import React, { useState } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field, useFormikContext } from 'formik';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ReactGA from 'react-ga';
import DataDialog from 'components/dialogs/DataDialog';
import Terms from 'containers/terms';
import { CheckCircleOutlineOutlined, ErrorOutlineOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { getCarrierBasicsByDOTNumber } from 'services/integration-service';
import LabelValueBox from 'components/LabelValueBox';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import { sendVerificationEmail, verifyEmail } from 'services/register-service';

const RegistrationForm = ({
  plan,
  newEntity,
  handleRegisterTenant,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const steps = ['Company Information', 'Email Verification', 'Finish Registration'];
  const [activeStep, setActiveStep] = useState(0);
  const [isValidDOT, setIsValidDOT] = useState(false);
  const [dotMessage, setDOTMessage] = useState('');
  const [isTermsAndPolicyDialogOpen, setTermsAndPolicyDialogOpen] = React.useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = (evt) => { evt.preventDefault(); setShowPassword((prev) => !prev); }

  /* API related state */
  const [apiToast, setAPIToast] = useState(null);
  const [isAPIProcessing, setIsAPIProcessing] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  
  const passwordChecklist = [
    { label: 'Min 8 characters', test: (val) => val.length >= 8 },
    { label: 'One uppercase letter', test: (val) => /[A-Z]/.test(val) },
    { label: 'One lowercase letter', test: (val) => /[a-z]/.test(val) },
    { label: 'One number', test: (val) => /[0-9]/.test(val) },
    { label: 'One special character', test: (val) => /[^A-Za-z0-9]/.test(val) },
  ];

  const validationSchema = [
    Yup.object({
      // dotNumber: Yup.string().max(100).required('Required Field.'),
      // email: Yup.string().email('Please enter valid email').max(100).required('Required Field'),
      // firstName: Yup.string().max(50).required('Required Field'),
      // lastName: Yup.string().max(50).required('Required Field'),
      // password: Yup.string().required('Please enter your password'),
      // confirmPassword: Yup.string().required('Please confirm your password')
    }),
    Yup.object({
      // verificationToken: Yup.string().min(6).max(6).required('Please enter your verification code.')
    }),
    Yup.object({
      policy: Yup.boolean().oneOf([true], 'This field must be checked')
    }),
  ];

  const handleDOTVerification = (evt, form, fieldValue) => {
    getCarrierBasicsByDOTNumber(fieldValue).then(res => {
      if (res.status === 200) {
        if (res.data && res.data.content) {
          const carrier = res.data.content.carrier;
          form.setFieldValue('company', carrier.legalName);
          form.setFieldValue('street', carrier.phyStreet);
          form.setFieldValue('city', carrier.phyCity);
          form.setFieldValue('zipcode', carrier.phyZipcode);
          form.setFieldValue('state', carrier.phyState);
          form.setFieldValue('country', carrier.phyCountry);
          setIsValidDOT(true);
        } else {
          setIsValidDOT(false);
          setDOTMessage('No company found. please check your DOT Number');
        }
      } else {
        setIsValidDOT(false);
        setDOTMessage('No company found. please check your DOT Number');
      }
    })
  };

  const StepOne = () => {
    return (
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant='caption'>{'Selected Plan'}</Typography>
          <Typography variant='subtitle1' sx={{fontWeight: 500}}>{plan.toUpperCase()}</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant='caption'>{'Transportation Mode'}</Typography>
          <Field name='businessModel'>
            {({ form, field, meta }) => (
              <ButtonGroup variant='contained'>
                <Button
                  id='fs-signup-otr'
                  onClick={() => form.setFieldValue('businessModel', 'OTR')}
                  color={`${field.value === 'OTR' ? 'secondary' : 'primary'}`}
                >{'OTR'}
                </Button>
                <Button
                  id='fs-signup-intermodal'
                  onClick={() => form.setFieldValue('businessModel', 'INTERMODAL')}
                  color={`${field.value === 'INTERMODAL' ? 'secondary' : 'primary'}`}
                >{'INTERMODAL'}
                </Button>
                <Button
                  id='fs-signup-both'
                  onClick={() => form.setFieldValue('businessModel', 'BOTH')}
                  color={`${field.value === 'BOTH' ? 'secondary' : 'primary'}`}
                >{'BOTH'}
                </Button>
              </ButtonGroup>
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Field name='dotNumber'>
            {({ form, field, meta }) => (
              <TextField
                fullWidth
                label='Enter DOT Number'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                onBlur={(evt) => handleDOTVerification(evt, form, field.value)}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Field name='mcNumber'>
            {({ field, meta }) => (
              <TextField
                fullWidth
                label='Enter MC Number'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box>
            {isValidDOT ?
              <Field>
                {({ form }) => (
                  <>
                    <Typography variant='body1'>{form.values['company']}</Typography>
                    <Typography variant='body2'>{form.values['street']}</Typography>
                    <Typography variant='body2'>{`${form.values['city']}, ${form.values['state']}, ${form.values['country']}`}</Typography>
                  </>
                )}
              </Field> :
              <Typography variant='body1' color={colors.error.main}>{dotMessage}</Typography>
            }
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Field name='email'>
            {({ field, meta }) => (
              <TextField
                fullWidth
                label='Enter Email'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Field name='firstName'>
            {({ field, meta }) => (
              <TextField
                fullWidth
                label='Enter your First Name'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Field name='lastName'>
            {({ field, meta }) => (
              <TextField
                fullWidth
                label='Enter your Last Name'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Field name='password'>
            {({ field, meta }) => (
              <TextField
                fullWidth
                label='Enter Password'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                type={showPassword ? 'text' : 'password'}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">
                      <IconButton
                        onClick={evt => togglePasswordVisibility(evt)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                }}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Field name='confirmPassword'>
            {({ field, meta }) => (
              <TextField
                fullWidth
                label='Confirm Password'
                variant='outlined'
                margin='dense'
                size='small'
                {...field}
                type={showPassword ? 'text' : 'password'}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                }}
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Field>
            {({ form }) => (
              <Stack direction={'row'} spacing={1}>
                {passwordChecklist.map(({ label, test }) => (
                  <Box display={'flex'} gap={.5}>
                    <Typography>{test(form.values.password) ?
                      <FTIcons.CheckCircleIcon width={20} height={20} fill={colors.success.main} /> :
                      <FTIcons.CrossCircleIcon width={20} height={20} fill={colors.error.main} />}
                    </Typography>
                    <Typography variant='caption'>{label}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Field>
        </Grid>
      </Grid>
    );
  };

  const StepTwo = () => {
    return (
      <Stack mt={3}>
        <Typography variant='subtitle1'>{'Enter verification code'}</Typography>
        <Box display={'flex'}>
          <Field name='verificationToken'>
            {({ field, meta }) => (
              <>
                <TextField
                  fullWidth={false}
                  label='Verification Code'
                  variant='outlined'
                  margin='dense'
                  size='small'
                  {...field}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
              </>
            )}
          </Field>
        </Box>
        <Box display={'flex'} gap={2}>
          <Typography variant='body1'>Didn't get a code ?
            <Button variant='text' color='secondary'>Click to resend</Button>
          </Typography>
        </Box>
      </Stack>
    );
  };

  const StepThree = () => {
    const values = useFormikContext();
    return (
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Typography variant='subtitle1' style={{ fontWeight: 500 }}>{'Review your information'} </Typography>
          <Typography variant='caption'>{'Click Finish Registration to create your account.'}</Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['company']}
                label='Company'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['dotNumber']}
                label='DOT Number'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['mcNumber']}
                label='MC Number'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['firstName']}
                label='First Name'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['lastName']}
                label='Last Name'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['email']}
                label='Email'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['accountPlan']}
                label='Account Plan'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Field>
            {({ form }) => (
              <LabelValueBox
                value={form.values['businessModel']}
                label='Transportation Mode'
              />
            )}
          </Field>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Field name='policy'>
            {({ field, meta }) => (
              <Box display={'flex'} gap={1} alignItems={'center'}>
                <Checkbox
                  {...field}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                />
                <Typography
                  color='textSecondary'
                  variant='body1'
                >
                  I have read the
                  {' '}
                  <Link
                    color='primary'
                    onClick={() => setTermsAndPolicyDialogOpen(true)}
                    underline='always'
                    variant='body1'
                  >
                    Terms and Conditions
                  </Link>
                </Typography>
                <DataDialog
                  open={isTermsAndPolicyDialogOpen}
                  title='Terms & Conditions'
                  width={'md'}
                  onCloseHandler={() => setTermsAndPolicyDialogOpen(false)}
                >
                  <Terms />
                </DataDialog>
              </Box>
            )}
          </Field>
        </Grid>
      </Grid>
    );
  }

  const getStepContent = (stepIndex, formik) => {
    const {field, form, values, meta} = formik;    
    switch (stepIndex) {
    case 0:
      return <StepOne
          form={form}
          field={field}
          values={values}
        />;
    case 1:
      return <StepTwo />;
    case 2:
      return <StepThree />;
    }
  };

  const getToastMessage = () => {
    if (apiToast && apiToast.type) {
      const { type, message } = apiToast;
      switch (type) {
      case 'EMAIL_ERROR':
        return <Box mt={2} mb={2} display={'flex'} justifyContent={'center'} color={colors.error.main}>
          <ErrorOutlineOutlined />&nbsp;&nbsp;<Typography variant='body1'>{message || 'Unable to send email. please contact our support team.'}</Typography>
        </Box>;
      case 'EMAIL_SUCCESS':
        return <Box mt={2} mb={2} display={'flex'} justifyContent={'center'} color={colors.success.main}>
          <CheckCircleOutlineOutlined />&nbsp;&nbsp;<Typography variant='body1'>{message}</Typography>
        </Box>;
      case 'VERIFY_ERROR':
        return <Box mt={2} mb={2} display={'flex'} justifyContent={'center'} color={colors.error.main}>
          <ErrorOutlineOutlined />&nbsp;&nbsp;<Typography variant='body1'>{message || 'Verification Code not matching. Please provide the correct verification code.'}</Typography>
        </Box>;
      case 'VERIFY_SUCCESS':
        return <Box mt={2} mb={2} display={'flex'} justifyContent={'center'} color={colors.success.main}>
          <CheckCircleOutlineOutlined />&nbsp;&nbsp;<Typography variant='body1'>{message || 'Token verified.'}</Typography>
        </Box>;
      }
    }
  };
  /* Media Query */
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Formik
      initialValues={{ ...newEntity }}
      enableReinitialize={true}
      validationSchema={validationSchema[activeStep]}
      onSubmit={async (values, actions) => {
        try {
          await validationSchema[activeStep].validate(values, { abortEarly: false });

          if (activeStep === steps.length - 1) {
            // Final step: submit form
            const trimmed_data = {};
            Object.keys(values).forEach(key => trimmed_data[key] = typeof (values[key]) === 'string' ? values[key].trim() : values[key]);
            handleRegisterTenant(trimmed_data);
            ReactGA.event({
              category: 'Registration',
              action: 'New User Registered Successfully.'
            });
          }
          actions.setTouched({});
          actions.setSubmitting(false);
        } catch (err) {
          const errors = {};
          const touched = {};
          err.inner.forEach((e) => {
            errors[e.path] = e.message;
            touched[e.path] = e.message;
          });
          actions.setErrors(errors);
          actions.setTouched(touched);
          actions.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form>
          <Stepper activeStep={activeStep} orientation='horizontal'>
            {steps.map((step, index) => {
              return (
                <Step key={index}>
                  <StepLabel>{step}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          <Box mt={2}>
            {isAPIProcessing && <CircularProgress sx={{textAlign: 'center' }} />}
            {getToastMessage()}
            {getStepContent(activeStep, formik)}
          </Box>
          <Box mt={2} display={'flex'} justifyContent={'flex-end'}>
            {activeStep > 0 && (<Button
              disabled={activeStep === 0}
              onClick={() => setActiveStep(prevStep => prevStep - 1)}
            >
              Back
            </Button>
            )}

            <Button
              id={`fs-signup-action-${activeStep}`}
              variant='contained'
              color='primary'
              type={activeStep === steps.length - 1 ? 'submit' : 'button'}
              onClick={async () => {
                if(activeStep === steps.length - 1) return;
                // ✅ Validate current step before advancing
                const errors = await formik.validateForm();
                const stepFields = Object.keys(
                  validationSchema[activeStep].fields
                );
                const stepErrors = stepFields.filter((f) => errors[f]);

                if (stepErrors.length === 0) {
                  setIsAPIProcessing(true);
                  setAPIToast({});
                  //API calls
                  if (activeStep === 0) {
                    /* 1. Send Verification Email */
                    setActiveStep((prev) => prev + 1);
                    // await sendVerificationEmail(formik.values.email)
                    //   .then(res => {
                    //     setIsAPIProcessing(false);
                    //     setAPIToast({type: 'EMAIL_SUCCESS', message: 'Email Sent. Please enter verification code here.'});
                    //     setActiveStep((prev) => prev + 1);
                    //   })
                    //   .catch(err => {
                    //     const { data } = err.response;
                    //     setAPIToast({type: 'EMAIL_ERROR', message: data && data.message});
                    //     setIsAPIProcessing(false);
                    //   })                      
                  } else {
                    /* 2. Verify Token */
                    setActiveStep((prev) => prev + 1);
                    // await verifyEmail(formik.values.email, formik.values.verificationToken)
                    //   .then(res => {
                    //     if(res.status === 200) {
                    //       const { data } = res;
                    //       setIsAPIProcessing(false);
                    //       setAPIToast({type: 'VERIFY_SUCCESS', message: data && data.message});
                    //       setActiveStep((prev) => prev + 1);
                    //     }
                    //   })
                    //   .catch(err => {
                    //     const { data } = err.response;
                    //     setIsAPIProcessing(false);
                    //     setAPIToast({type: 'VERIFY_ERROR', message: data && data.message});
                    //   });
                  }
                } else {
                  stepErrors.forEach((f) =>
                    formik.setFieldTouched(f, true, false)
                  );
                }
              }}
            >
              {activeStep === steps.length - 1 ? 'Finish Registration' : 'Next'}
            </Button>            
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default RegistrationForm;